import { CircleAlert, CircleCheck, CircleX, Info } from "lucide-preact";
import { useEffect, useState } from "preact/hooks";

export type ToastVariant = "success" | "error" | "info" | "warning";

export type ToastProps = {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  onClose: (id: string) => void;
};

const variantConfig = {
  success: {
    icon: CircleCheck,
    className: "bg-green-500/30 dark:bg-green-900/30 border-green-500/40 dark:border-green-700 text-green-900 dark:text-green-100",
    iconClassName: "text-green-700 dark:text-green-400",
  },
  error: {
    icon: CircleX,
    className: "bg-red-500/30 dark:bg-red-900/30 border-red-500/40 dark:border-red-700 text-red-900 dark:text-red-100",
    iconClassName: "text-red-700 dark:text-red-400",
  },
  info: {
    icon: Info,
    className: "bg-blue-500/30 dark:bg-blue-900/30 border-blue-500/40 dark:border-blue-700 text-blue-900 dark:text-blue-100",
    iconClassName: "text-blue-700 dark:text-blue-400",
  },
  warning: {
    icon: CircleAlert,
    className: "bg-yellow-500/30 dark:bg-yellow-900/30 border-yellow-500/40 dark:border-yellow-700 text-yellow-900 dark:text-yellow-100",
    iconClassName: "text-yellow-700 dark:text-yellow-400",
  },
};

const EXIT_MS = 300;

export const Toast = ({ id, message, variant, duration = 5000, onClose }: ToastProps) => {
  const [isExiting, setIsExiting] = useState(false);
  const config = variantConfig[variant];
  const Icon = config.icon;

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(id), EXIT_MS);
  };

  useEffect(() => {
    const timer = setTimeout(handleClose, duration);
    return () => clearTimeout(timer);
  }, [duration, id]);

  return (
    <div
      data-testid={id}
      data-toast-variant={variant}
      role="alertdialog"
      onClick={handleClose}
      class={`max-w-lg mx-auto flex items-start space-x-3 p-4 rounded-lg border shadow-lg backdrop-blur-sm cursor-pointer transition-all duration-300 ${
        config.className
      } ${isExiting ? "opacity-0 translate-x-full" : "opacity-100 translate-x-0 hover:shadow-xl"}`}
    >
      <Icon size={20} class={`flex-shrink-0 mt-0.5 ${config.iconClassName}`} />
      <p class="flex-1 text-sm font-medium">{message}</p>
    </div>
  );
};
